import {
  BriefcaseBusiness,
  CalendarDays,
  CalendarRange,
  Check,
  Clipboard,
  Download,
  Link2,
  Pencil,
  Plus,
  RotateCw,
  School,
  ShieldCheck,
  SlidersHorizontal,
  SunMedium,
  Trash2,
  UserRound,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import OrganizationPolicySettings from "../components/OrganizationPolicySettings";
import HolidaySettings from "../components/HolidaySettings";
import PageHeader from "../components/PageHeader";
import SchoolVacationsSettings from "../components/SchoolVacationsSettings";
import WorkAndLeaveSettings from "../components/WorkAndLeaveSettings";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import type { Paginated } from "../types/api";
import type { CalendarFeed, LeaveType } from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate } from "../utils/format";
import DateField from "../components/DateField";

type Tab =
  | "profile"
  | "policy"
  | "work"
  | "school-vacations"
  | "holidays"
  | "calendar"
  | "feeds"
  | "privacy";
type Profile = {
  school_region: string;
  optional_holidays_enabled: boolean;
  rounding_minutes: number;
};
type CalendarEvent = {
  id: number;
  event_type: string;
  title: string;
  start_date: string;
  end_date: string;
  annual_recurrence: boolean;
  note: string;
};
type Note = { id: number; date: string; text: string };
type FeedDraft = {
  id: number | null;
  name: string;
  scope: CalendarFeed["scope"];
  organization: number | null;
  statuses: CalendarFeed["statuses"];
  detail_level: CalendarFeed["detail_level"];
  leave_types: number[];
  is_active: boolean;
};

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { organizationId, organization, organizations, capabilities } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab") as Tab | null;
  const [tab, setTab] = useState<Tab>(
    requestedTab &&
      [
        "profile",
        "policy",
        "work",
        "school-vacations",
        "holidays",
        "calendar",
        "feeds",
        "privacy",
      ].includes(requestedTab)
      ? requestedTab
      : "profile",
  );
  const [profile, setProfile] = useState<Profile | null>(null);
  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [lastName, setLastName] = useState(user?.last_name ?? "");
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [eventType, setEventType] = useState("birthday");
  const [annualRecurrence, setAnnualRecurrence] = useState(true);
  const [eventTitle, setEventTitle] = useState("");
  const [eventDate, setEventDate] = useState(new Date().toISOString().slice(0, 10));
  const [noteText, setNoteText] = useState("");
  const [feeds, setFeeds] = useState<CalendarFeed[]>([]);
  const [feedLeaveTypes, setFeedLeaveTypes] = useState<LeaveType[]>([]);
  const [feedDraft, setFeedDraft] = useState<FeedDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const settingsNavRef = useRef<HTMLElement>(null);

  const calendarSuffix = organizationId
    ? `?organization=${organizationId}`
    : "?context=personal";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        profileResponse,
        eventResponse,
        noteResponse,
        feedResponse,
        feedTypeResponse,
      ] = await Promise.all([
        api.get<Profile>("/onboarding/profile/"),
        api.get<Paginated<CalendarEvent> | CalendarEvent[]>(
          `/leave/calendar-events/${calendarSuffix}`,
        ),
        api.get<Paginated<Note> | Note[]>("/leave/personal-notes/"),
        api.get<Paginated<CalendarFeed> | CalendarFeed[]>("/ics/feeds/"),
        api.get<Paginated<LeaveType> | LeaveType[]>("/leave/types/?scope=all"),
      ]);
      setProfile(profileResponse.data);
      setEvents(unwrapResults(eventResponse.data));
      setNotes(unwrapResults(noteResponse.data));
      setFeeds(unwrapResults(feedResponse.data));
      setFeedLeaveTypes(unwrapResults(feedTypeResponse.data));
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De instellingen konden niet worden geladen."),
      );
    } finally {
      setLoading(false);
    }
  }, [calendarSuffix]);

  useEffect(() => {
    void load();
  }, [load]);
  const chooseTab = (next: Tab) => {
    setTab(next);
    setSearchParams(next === "profile" ? {} : { tab: next });
    setError("");
    setSuccess("");
  };

  useEffect(() => {
    if (!organizationId && tab === "policy") {
      setTab("profile");
      setSearchParams({});
    }
  }, [organizationId, setSearchParams, tab]);

  useEffect(() => {
    const navigation = settingsNavRef.current;
    const activeItem = navigation?.querySelector<HTMLElement>(".active");
    if (navigation && activeItem && navigation.scrollWidth > navigation.clientWidth) {
      activeItem.scrollIntoView({ block: "nearest", inline: "center" });
    }
  }, [loading, organizationId, tab]);

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (!profile) return;
    try {
      await Promise.all([
        api.patch("/auth/me/", { first_name: firstName, last_name: lastName }),
        api.patch("/onboarding/profile/", {
          optional_holidays_enabled: profile.optional_holidays_enabled,
          rounding_minutes: profile.rounding_minutes,
        }),
      ]);
      await refreshUser();
      setSuccess("Profiel opgeslagen.");
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het profiel kon niet worden opgeslagen."),
      );
    }
  };

  const createEvent = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api.post("/leave/calendar-events/", {
        organization: organizationId,
        event_type: eventType,
        title: eventTitle,
        start_date: eventDate,
        end_date: eventDate,
        annual_recurrence: annualRecurrence,
      });
      setEventTitle("");
      setSuccess("Kalendergebeurtenis toegevoegd.");
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De gebeurtenis kon niet worden toegevoegd."),
      );
    }
  };

  const createNote = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api.post("/leave/personal-notes/", { date: eventDate, text: noteText });
      setNoteText("");
      setSuccess("Notitie toegevoegd.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De notitie kon niet worden toegevoegd."));
    }
  };

  const removeCalendarItem = async (
    kind: "calendar-events" | "personal-notes",
    id: number,
  ) => {
    if (!window.confirm("Dit kalenderitem verwijderen?")) return;
    await api.delete(`/leave/${kind}/${id}/`);
    await load();
  };

  const newFeed = () => {
    setFeedDraft({
      id: null,
      name: "",
      scope: "all",
      organization: null,
      statuses: ["draft", "requested", "approved"],
      detail_level: "detailed",
      leave_types: [],
      is_active: true,
    });
  };

  const editFeed = (feed: CalendarFeed) => {
    setFeedDraft({
      id: feed.id,
      name: feed.name,
      scope: feed.scope,
      organization: feed.organization,
      statuses: [...feed.statuses],
      detail_level: feed.detail_level,
      leave_types: [...feed.leave_types],
      is_active: feed.is_active,
    });
  };

  const saveFeed = async (event: FormEvent) => {
    event.preventDefault();
    if (!feedDraft) return;
    setError("");
    try {
      const payload = {
        ...feedDraft,
        organization:
          feedDraft.scope === "organization" ? feedDraft.organization : null,
      };
      if (feedDraft.id) {
        await api.patch(`/ics/feeds/${feedDraft.id}/`, payload);
      } else {
        await api.post("/ics/feeds/", payload);
      }
      setFeedDraft(null);
      setSuccess("De agenda-koppeling is opgeslagen.");
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(
          requestError,
          "De agenda-koppeling kon niet worden opgeslagen.",
        ),
      );
    }
  };

  const rotateFeed = async (feed: CalendarFeed) => {
    if (!window.confirm(`De huidige link voor ${feed.name} direct intrekken?`)) return;
    try {
      await api.post(`/ics/feeds/${feed.id}/rotate/`);
      setSuccess("Er is een nieuwe geheime link gemaakt.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De link kon niet worden vervangen."));
    }
  };

  const removeFeed = async (feed: CalendarFeed) => {
    if (!window.confirm(`${feed.name} verwijderen en de link intrekken?`)) return;
    try {
      await api.delete(`/ics/feeds/${feed.id}/`);
      setSuccess("De agenda-koppeling is verwijderd.");
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(
          requestError,
          "De agenda-koppeling kon niet worden verwijderd.",
        ),
      );
    }
  };

  const copyFeedUrl = async (feed: CalendarFeed) => {
    await navigator.clipboard.writeText(`${window.location.origin}${feed.feed_path}`);
    setSuccess("De geheime agenda-link staat op je klembord.");
  };

  const exportPersonalData = async () => {
    try {
      const response = await api.get("/auth/export/", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `verlofkaart-export-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
      setSuccess("Je persoonlijke export is aangemaakt.");
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De export kon niet worden aangemaakt."));
    }
  };

  if (loading) return <LoadingState label="Instellingen laden..." />;

  return (
    <div className="settings-page">
      <PageHeader
        eyebrow={organization?.name ?? "Persoonlijk"}
        title="Instellingen"
        description="Beheer je profiel, werk & verlof, kalenderkoppelingen en privacy."
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      <div className="settings-layout">
        <nav
          ref={settingsNavRef}
          className="settings-nav"
          aria-label="Instellingencategorieën"
        >
          <button
            className={tab === "profile" ? "active" : ""}
            onClick={() => chooseTab("profile")}
          >
            <UserRound size={18} /> Profiel
          </button>
          {organizationId && (
            <button
              className={tab === "policy" ? "active" : ""}
              onClick={() => chooseTab("policy")}
            >
              <SlidersHorizontal size={18} /> Verlofbeleid
            </button>
          )}
          <button
            className={tab === "work" ? "active" : ""}
            onClick={() => chooseTab("work")}
          >
            <BriefcaseBusiness size={18} /> Werk & verlof
          </button>
          <button
            className={tab === "school-vacations" ? "active" : ""}
            onClick={() => chooseTab("school-vacations")}
          >
            <School size={18} /> Schoolvakanties
          </button>
          <button
            className={tab === "holidays" ? "active" : ""}
            onClick={() => chooseTab("holidays")}
          >
            <SunMedium size={18} /> Feestdagen
          </button>
          <button
            className={tab === "calendar" ? "active" : ""}
            onClick={() => chooseTab("calendar")}
          >
            <CalendarDays size={18} /> Kalender
          </button>
          <button
            className={tab === "feeds" ? "active" : ""}
            onClick={() => chooseTab("feeds")}
          >
            <CalendarRange size={18} /> Agenda koppelen
          </button>
          <button
            className={tab === "privacy" ? "active" : ""}
            onClick={() => chooseTab("privacy")}
          >
            <ShieldCheck size={18} /> Eigen gegevens
          </button>
          {organization && (
            <Link to="/app/settings/abonnement">
              <Link2 size={18} /> Abonnement
            </Link>
          )}
        </nav>
        <div className="settings-content">
          {tab === "profile" && profile && (
            <form className="surface settings-section" onSubmit={saveProfile}>
              <div className="settings-heading">
                <h2>Profiel</h2>
                <p>
                  Deze gegevens worden gebruikt om je in de app herkenbaar weer te
                  geven.
                </p>
              </div>
              <div className="form-row">
                <label>
                  <span>Voornaam</span>
                  <input
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                  />
                </label>
                <label>
                  <span>Achternaam</span>
                  <input
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                  />
                </label>
              </div>
              <label>
                <span>E-mailadres</span>
                <input value={user?.email ?? ""} disabled />
              </label>
              <div className="form-row">
                <label>
                  <span>Afronding</span>
                  <select
                    value={profile.rounding_minutes}
                    onChange={(event) =>
                      setProfile({
                        ...profile,
                        rounding_minutes: Number(event.target.value),
                      })
                    }
                  >
                    <option value="15">15 minuten</option>
                    <option value="30">30 minuten</option>
                    <option value="60">60 minuten</option>
                  </select>
                </label>
              </div>
              <label className="toggle-field">
                <input
                  type="checkbox"
                  checked={profile.optional_holidays_enabled}
                  onChange={(event) =>
                    setProfile({
                      ...profile,
                      optional_holidays_enabled: event.target.checked,
                    })
                  }
                />
                <span>
                  <strong>Optionele feestdagen tonen</strong>
                  <small>Dit verandert niet automatisch je saldo.</small>
                </span>
              </label>
              <button className="button primary" type="submit">
                <Check size={17} /> Opslaan
              </button>
            </form>
          )}
          {tab === "policy" && organizationId && (
            <OrganizationPolicySettings
              organizationId={organizationId}
              canManage={
                capabilities.canManageOrganizationPolicies &&
                !capabilities.organizationReadOnly
              }
            />
          )}
          {tab === "work" && <WorkAndLeaveSettings />}
          {tab === "school-vacations" && <SchoolVacationsSettings />}
          {tab === "holidays" && (
            <HolidaySettings
              organizationId={organizationId}
              canManage={
                !organizationId ||
                (capabilities.canManageOrganizationPolicies &&
                  !capabilities.organizationReadOnly)
              }
            />
          )}
          {tab === "calendar" && (
            <>
              <div className="settings-double">
                <form className="surface settings-section" onSubmit={createEvent}>
                  <div className="settings-heading">
                    <h2>
                      {organization
                        ? "Organisatiegebeurtenis toevoegen"
                        : "Gebeurtenis toevoegen"}
                    </h2>
                    <p>Informatief, zonder saldo-impact.</p>
                  </div>
                  <label>
                    <span>Soort</span>
                    <select
                      value={eventType}
                      onChange={(event) => {
                        const nextType = event.target.value;
                        setEventType(nextType);
                        setAnnualRecurrence(nextType === "birthday");
                      }}
                    >
                      <option value="birthday">Verjaardag</option>
                      <option value="study_day">Studiedag</option>
                      <option value="childcare">Opvang / BSO</option>
                      <option value="family">Familieafspraak</option>
                      <option value="reminder">Herinnering</option>
                      <option value="other">Overig</option>
                    </select>
                  </label>
                  <label>
                    <span>Titel</span>
                    <input
                      value={eventTitle}
                      onChange={(event) => setEventTitle(event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Datum</span>
                    <DateField
                      value={eventDate}
                      onChange={(value) => setEventDate(value)}
                      required
                    />
                  </label>
                  <label className="toggle-field">
                    <input
                      type="checkbox"
                      checked={annualRecurrence}
                      onChange={(event) => setAnnualRecurrence(event.target.checked)}
                    />
                    <span>
                      <strong>Jaarlijks herhalen</strong>
                      <small>Ook in volgende jaren tonen</small>
                    </span>
                  </label>
                  <button
                    className="button primary"
                    type="submit"
                    disabled={Boolean(
                      organizationId && !capabilities.canManageOrganizationPolicies,
                    )}
                  >
                    <Plus size={17} /> Toevoegen
                  </button>
                  {organizationId && !capabilities.canManageOrganizationPolicies && (
                    <p className="field-hint">
                      Alleen een organisatiebeheerder kan organisatiegebeurtenissen
                      toevoegen.
                    </p>
                  )}
                </form>
                <form className="surface settings-section" onSubmit={createNote}>
                  <div className="settings-heading">
                    <h2>Persoonlijke notitie</h2>
                    <p>Alleen zichtbaar in je eigen planner.</p>
                  </div>
                  <label>
                    <span>Datum</span>
                    <DateField
                      value={eventDate}
                      onChange={(value) => setEventDate(value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Notitie</span>
                    <textarea
                      rows={4}
                      value={noteText}
                      onChange={(event) => setNoteText(event.target.value)}
                      required
                    />
                  </label>
                  <button className="button primary" type="submit">
                    <Plus size={17} /> Notitie toevoegen
                  </button>
                </form>
              </div>
              <section className="surface settings-section">
                <div className="settings-heading">
                  <h2>Kalenderitems</h2>
                </div>
                <div className="calendar-settings-list">
                  {[
                    ...events.map((item) => ({
                      ...item,
                      kind: "calendar-events" as const,
                      date: item.start_date,
                      label: item.title,
                    })),
                    ...notes.map((item) => ({
                      ...item,
                      kind: "personal-notes" as const,
                      label: item.text,
                    })),
                  ]
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map((item) => (
                      <article key={`${item.kind}-${item.id}`}>
                        <span>
                          <strong>{item.label}</strong>
                          <small>{formatDate(item.date)}</small>
                        </span>
                        <button
                          className="icon-button danger"
                          type="button"
                          title="Verwijderen"
                          aria-label="Verwijderen"
                          onClick={() => void removeCalendarItem(item.kind, item.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </article>
                    ))}
                </div>
              </section>
            </>
          )}
          {tab === "feeds" && (
            <>
              {feedDraft && (
                <form
                  className="surface settings-section feed-editor"
                  onSubmit={saveFeed}
                >
                  <div className="settings-heading">
                    <h2>
                      {feedDraft.id ? "Agenda-koppeling wijzigen" : "Agenda koppelen"}
                    </h2>
                    <p>
                      Voor Apple Agenda, Google Agenda, Outlook en andere agenda-apps
                      met iCal.
                    </p>
                  </div>
                  <div className="form-row">
                    <label>
                      <span>Naam</span>
                      <input
                        value={feedDraft.name}
                        maxLength={80}
                        placeholder="Bijvoorbeeld Werkagenda"
                        onChange={(event) =>
                          setFeedDraft({ ...feedDraft, name: event.target.value })
                        }
                        required
                      />
                    </label>
                    <label>
                      <span>Werkruimte</span>
                      <select
                        value={feedDraft.scope}
                        onChange={(event) => {
                          const scope = event.target.value as CalendarFeed["scope"];
                          setFeedDraft({
                            ...feedDraft,
                            scope,
                            organization:
                              scope === "organization"
                                ? feedDraft.organization ?? organizations[0]?.id ?? null
                                : null,
                          });
                        }}
                      >
                        <option value="all">Alle werkruimtes</option>
                        <option value="personal">Alleen persoonlijk</option>
                        <option
                          value="organization"
                          disabled={organizations.length === 0}
                        >
                          Eén organisatie
                        </option>
                      </select>
                    </label>
                  </div>
                  {feedDraft.scope === "organization" && (
                    <label>
                      <span>Organisatie</span>
                      <select
                        value={feedDraft.organization ?? ""}
                        onChange={(event) =>
                          setFeedDraft({
                            ...feedDraft,
                            organization: Number(event.target.value),
                          })
                        }
                        required
                      >
                        {organizations.map((row) => (
                          <option key={row.id} value={row.id}>
                            {row.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <fieldset className="feed-options">
                    <legend>Statussen</legend>
                    {(
                      [
                        ["draft", "Concept"],
                        ["requested", "Aangevraagd"],
                        ["approved", "Goedgekeurd"],
                      ] as const
                    ).map(([value, label]) => (
                      <label key={value} className="check-field">
                        <input
                          type="checkbox"
                          checked={feedDraft.statuses.includes(value)}
                          onChange={() =>
                            setFeedDraft({
                              ...feedDraft,
                              statuses: feedDraft.statuses.includes(value)
                                ? feedDraft.statuses.filter(
                                    (status) => status !== value,
                                  )
                                : [...feedDraft.statuses, value],
                            })
                          }
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </fieldset>
                  <fieldset className="feed-options">
                    <legend>Weergave</legend>
                    <label className="check-field">
                      <input
                        type="radio"
                        name="detail-level"
                        checked={feedDraft.detail_level === "detailed"}
                        onChange={() =>
                          setFeedDraft({ ...feedDraft, detail_level: "detailed" })
                        }
                      />
                      <span>Verloftype en status</span>
                    </label>
                    <label className="check-field">
                      <input
                        type="radio"
                        name="detail-level"
                        checked={feedDraft.detail_level === "free_busy"}
                        onChange={() =>
                          setFeedDraft({ ...feedDraft, detail_level: "free_busy" })
                        }
                      />
                      <span>Alleen “Afwezig”</span>
                    </label>
                  </fieldset>
                  <fieldset className="feed-options feed-type-options">
                    <legend>Verloftypen</legend>
                    {feedLeaveTypes.map((leaveType) => (
                      <label key={leaveType.id} className="check-field">
                        <input
                          type="checkbox"
                          checked={feedDraft.leave_types.includes(leaveType.id)}
                          onChange={() =>
                            setFeedDraft({
                              ...feedDraft,
                              leave_types: feedDraft.leave_types.includes(leaveType.id)
                                ? feedDraft.leave_types.filter(
                                    (id) => id !== leaveType.id,
                                  )
                                : [...feedDraft.leave_types, leaveType.id],
                            })
                          }
                        />
                        <span>{leaveType.name}</span>
                      </label>
                    ))}
                  </fieldset>
                  <label className="toggle-field">
                    <input
                      type="checkbox"
                      checked={feedDraft.is_active}
                      onChange={(event) =>
                        setFeedDraft({ ...feedDraft, is_active: event.target.checked })
                      }
                    />
                    <span>
                      <strong>Link actief</strong>
                      <small>Uitgeschakeld geeft de agenda-app geen gegevens.</small>
                    </span>
                  </label>
                  <div className="form-actions">
                    <button
                      className="button subtle"
                      type="button"
                      onClick={() => setFeedDraft(null)}
                    >
                      Annuleren
                    </button>
                    <button
                      className="button primary"
                      type="submit"
                      disabled={feedDraft.statuses.length === 0}
                    >
                      <Check size={17} /> Koppeling opslaan
                    </button>
                  </div>
                </form>
              )}
              <section className="surface settings-section feed-list-section">
                <div className="settings-heading feed-list-heading">
                  <div>
                    <h2>Mijn agenda-koppelingen</h2>
                    <p>
                      Iedere koppeling heeft een eigen geheime link en eigen
                      zichtbaarheid.
                    </p>
                  </div>
                  <button className="button primary" type="button" onClick={newFeed}>
                    <Plus size={17} /> Nieuwe koppeling
                  </button>
                </div>
                {feeds.length === 0 ? (
                  <div className="empty-state compact">
                    <CalendarRange size={27} />
                    <h2>Nog geen agenda gekoppeld</h2>
                  </div>
                ) : (
                  <div className="feed-list">
                    {feeds.map((feed) => (
                      <article
                        key={feed.id}
                        className={!feed.is_active ? "is-inactive" : ""}
                      >
                        <div className="feed-summary">
                          <span className="feed-icon">
                            <CalendarRange size={18} />
                          </span>
                          <div>
                            <strong>{feed.name}</strong>
                            <small>
                              {feed.scope === "all"
                                ? "Alle werkruimtes"
                                : feed.scope === "personal"
                                ? "Persoonlijk"
                                : organizations.find(
                                    (row) => row.id === feed.organization,
                                  )?.name ?? "Organisatie"}
                              {" · "}
                              {feed.detail_level === "free_busy"
                                ? "Alleen afwezig"
                                : "Met details"}
                            </small>
                          </div>
                          {!feed.is_active && (
                            <span className="member-status status-ended">Uit</span>
                          )}
                        </div>
                        <div className="secret-output">
                          <code>{`${window.location.origin}${feed.feed_path}`}</code>
                          <button
                            className="icon-button"
                            type="button"
                            title="Link kopiëren"
                            aria-label={`${feed.name} kopiëren`}
                            onClick={() => void copyFeedUrl(feed)}
                          >
                            <Clipboard size={17} />
                          </button>
                        </div>
                        <div className="feed-meta">
                          {feed.statuses.map((status) => (
                            <span key={status}>
                              {status === "draft"
                                ? "Concept"
                                : status === "requested"
                                ? "Aangevraagd"
                                : "Goedgekeurd"}
                            </span>
                          ))}
                          <span>
                            {feed.leave_type_names.length
                              ? feed.leave_type_names.join(", ")
                              : "Alle verloftypen"}
                          </span>
                        </div>
                        <div className="feed-actions">
                          <button
                            className="icon-button"
                            type="button"
                            title="Wijzigen"
                            aria-label={`${feed.name} wijzigen`}
                            onClick={() => editFeed(feed)}
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="icon-button"
                            type="button"
                            title="Geheime link vervangen"
                            aria-label={`Link voor ${feed.name} vervangen`}
                            onClick={() => void rotateFeed(feed)}
                          >
                            <RotateCw size={16} />
                          </button>
                          <button
                            className="icon-button danger"
                            type="button"
                            title="Verwijderen"
                            aria-label={`${feed.name} verwijderen`}
                            onClick={() => void removeFeed(feed)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
          {tab === "privacy" && (
            <>
              <section className="surface settings-section">
                <div className="settings-heading">
                  <h2>Eigen gegevens</h2>
                  <p>
                    Download je persoonlijke verlofkaart als JSON-bestand.
                    Organisatiegegevens en geheime tokens worden niet opgenomen.
                  </p>
                </div>
                <button
                  className="button subtle"
                  type="button"
                  onClick={() => void exportPersonalData()}
                >
                  <Download size={17} /> Persoonlijke gegevens exporteren
                </button>
                <div className="inline-links">
                  <Link to="/privacy">Privacyverklaring</Link>
                  <Link to="/algemene-voorwaarden">Algemene voorwaarden</Link>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
